import { each, isEmpty } from 'lodash';
import qs from 'qs';
import { useEffect } from 'react';

import { api } from 'api';
import { isRouteGet } from 'helpers';
import { useAbortController, useSafeState, useUser } from 'hooks';

const errorStatus = [400, 401, 403, 404, 422, 500];

const addCompanyRoleFilters = (apiQuery, payload, currentUser) => ({
  ...apiQuery,
  filters: {
    /* filterUserCompany = true : get only content from user's company and user's company role */
    companies: {
      castelis_id: {
        $eq: currentUser.businessId
      }
    },
    statuses: {
      castelis_id: {
        $eq: currentUser.businessRoleId
      }
    },
    ...payload.query?.filters
  }
});

const formatCartParams = (apiQuery) => {
  const cartQuery = qs.stringify(apiQuery, {
    encodeValuesOnly: true,
    filter: ['articleId', 'typeItem', 'onPlan']
  });

  /*
    Placement array of object can not be stringify with qs lib
    because it is not compatible with the format expected by the API
  */
  let placementQuery = '';

  each(apiQuery.placement, (placement) => {
    placementQuery += `&placement[]=${JSON.stringify(placement)}`;
  });

  return `${cartQuery}${placementQuery}`;
};

export const useFetch = (fetchOptions = {}) => {
  const [loading, setLoading] = useSafeState(false);
  const [error, setError] = useSafeState(null);
  const [data, setData] = useSafeState(null);
  const { cancelRequest, getSignal: signal } = useAbortController();
  const { currentUser } = useUser();

  useEffect(() => {
    return () => {
      cancelRequest();
    };
  }, []);

  const call = async (payload, config = {}) => {
    const options = {
      filterUserCompany: false,
      withSetState: true,
      ...fetchOptions
    };

    try {
      setLoading(true);

      const isStrapiRequest = payload.service === 'strapi_touloisirs' || payload.service === 'strapi_arrivaj';
      let apiQuery = {
        /* populate = deep : get deep nested strapi content (components, relations) */
        ...(isStrapiRequest && { populate: 'deep' }),
        ...payload.query
      };

      if (currentUser && options?.filterUserCompany) {
        apiQuery = addCompanyRoleFilters(apiQuery, payload, currentUser);
      }

      let query = '';

      if (payload.path.includes('/order/caddy') && payload.method === 'post') {
        query = formatCartParams(apiQuery);
      } else {
        query = qs.stringify(apiQuery, { encodeValuesOnly: true });
      }

      const endpoint = payload.service === 'zendesk' ? '/zendesk' : '/proxy';

      const method = isRouteGet(payload.path) || isStrapiRequest ? 'get' : 'post';
      const body = method === 'get' ? { params: { ...payload, query } } : { ...payload, query };
      const { data: res } = (await api[method](endpoint, body, { ...config, signal: signal() })) || {};

      if (!isEmpty(res?.body)) {
        options?.withSetState && setData(res.body);
      }

      if (res?.status) {
        setLoading(false);
        errorStatus.includes(res.status) && setError(res.body);
      }

      return { data: res };
    } catch (error) {
      setError(error);
      setLoading(false);
    }
  };
  return { call, data, setData, loading, error };
};
